import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth-store';
import { Loading } from '@/components/loading';

interface AuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requireVerification?: boolean;
}

export function AuthGuard({
  children,
  requireAuth = true,
  requireVerification = true,
}: AuthGuardProps) {
  const location = useLocation();
  const { isAuthenticated, user, isLoading, checkAuth } = useAuthStore();

  useEffect(() => {
    // Check auth status on mount
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show loading while checking authentication
  if (isLoading) {
    return <Loading />;
  }

  // If authentication is required and user is not authenticated
  if (requireAuth && !isAuthenticated) {
    // Redirect to login page, but save the location they were trying to go to
    return <Navigate to='/auth/login' state={{ from: location }} replace />;
  }

  // Check if user needs email verification
  if (
    requireAuth &&
    isAuthenticated &&
    requireVerification &&
    user &&
    !user.isVerified
  ) {
    return (
      <Navigate
        to='/auth/email-verification'
        state={{ email: user.email }}
        replace
      />
    );
  }

  // If not requiring auth (public/auth pages) but user is authenticated
  // Redirect to dashboard to prevent authenticated users from accessing auth pages
  if (!requireAuth && isAuthenticated) {
    return <Navigate to='/dashboard' replace />;
  }

  // If requiring auth and user is authenticated, let them through
  if (requireAuth && isAuthenticated) {
    return <>{children}</>;
  }

  // For public routes when not authenticated
  return <>{children}</>;
}
